/**
 * PublicProfilePage — unauthenticated, read-only profile view.
 *
 * Accessible at /p/:userId without login. Shows the user's profile card
 * (same layout as UserDetailModal) with all interactive actions replaced
 * by a "Sign up / Log in" call-to-action. Navigating anywhere else hits
 * the existing ProtectedRoute guard which redirects to /login.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { WebOnly } from './ui/WebOnly';
import { Loader2, ArrowRight, Users } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { User } from '../types';
import { useTheme } from './ThemeProvider';
import { DEFAULT_BANNER_URL } from '../constants/assets';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { FlagImage } from './utils/countryFlags';
import { IdentityTag } from './IdentityTag';
import { PATHS } from '../router/paths';
import RankBadge from './RankBadge';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';

// ── App store links ───────────────────────────────────────────────────────────
// TODO: replace the placeholder Apple ID below with AfroYap's real App Store
// listing once it's live (format: https://apps.apple.com/app/afroyap/id<APPLE_ID>).
// Bundle id is com.afroyap.app (see capacitor.config.ts).
const APP_STORE_URL = 'https://apps.apple.com/app/afroyap/id0000000000';
// Not on Google Play yet — set this to the Play listing URL to surface the badge.
const PLAY_STORE_URL: string | null = null;
// Official Apple badge artwork (white variant — intended for dark backgrounds).
const APP_STORE_BADGE = 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/white/en-us?size=250x83';
const PLAY_STORE_BADGE = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';

export function PublicProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [user, setUser]       = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!userId) { setError(true); setLoading(false); return; }

    // If the visitor is already logged in, redirect to the in-app profile instead
    // of showing the public read-only view. Uses getAuth() (synchronous singleton)
    // so the redirect fires as soon as Firebase auth resolves — not relying on
    // the async-loaded userProfile context which may be null momentarily.
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        unsubscribe();
        if (firebaseUser.uid === userId) {
          navigate(PATHS.PROFILE, { replace: true });
        } else {
          navigate(`/profile/${userId}`, { replace: true });
        }
      }
    });

    (async () => {
      // Brief pause to let auth state resolve before fetching public profile data.
      // If auth resolved above (logged in), the navigate already happened and this
      // component will unmount, so the fetch below is a no-op.
      await new Promise(r => setTimeout(r, 300));
      if (!auth.currentUser) {
        unsubscribe();
        try {
          // Read via CF — never reads Firestore directly from an unauthenticated client.
          // getPublicProfile returns only display-safe fields; raw dateOfBirth is excluded.
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { getApp } = await import('firebase/app');
          const fn = httpsCallable(getFunctions(getApp()), 'getPublicProfile');
          const result = await fn({ userId });
          const data = result.data as any;
          if (data) {
            setUser({ ...data, id: userId } as User);
          } else {
            setError(true);
          }
        } catch {
          setError(true);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [userId]);

  const goToSignup = () => navigate(`${PATHS.SIGNUP}?redirect=/p/${userId}`);
  const goToLogin  = () => navigate(`${PATHS.LOGIN}?redirect=/p/${userId}`);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
        <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#0D0D0D] text-white px-6 text-center">
        <p className="text-lg font-semibold">Profile not found</p>
        <p className="text-white/50 text-sm">This profile may not exist or has been removed.</p>
        <button onClick={goToSignup} className="mt-4 px-6 py-3 rounded-full bg-[#4478ea] text-white font-bold text-sm">
          Join AfroYap
        </button>
      </div>
    );
  }

  const coverImage = user.bannerImage || DEFAULT_BANNER_URL;
  const age = typeof user.age === 'number' ? user.age : null; // CF-computed, no raw DOB
  const displayHandle = (user.username || '').replace(/^@/, '');
  const tags = [...(user.identities || []), ...(user.interests || [])].slice(0, 10);
  const portfolioImages = (user.portfolioPreviews || user.portfolio || []).filter(Boolean);
  const friendsCount = typeof (user as any).friendsCount === 'number' ? (user as any).friendsCount : null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col items-center">
      {/* Minimal header */}
      <div className="w-full max-w-[600px] flex items-center justify-between px-4 py-3">
        <img src={logoImageWhite as any} alt="AfroYap" className="h-6 w-auto" />
        <button
          onClick={goToLogin}
          className="text-sm text-white/60 hover:text-white transition-colors font-medium"
        >
          Log in
        </button>
      </div>

      {/* Profile card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[600px] flex-1"
      >
        {/* Banner */}
        <div className="relative w-full h-44 overflow-hidden">
          <img src={coverImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60" />
        </div>

        {/* Avatar + name row */}
        <div className="px-5 pb-4 bg-[#111] -mt-1">
          <div className="flex items-end gap-4 -mt-10 mb-3">
            <Avatar className="w-20 h-20 border-4 border-[#111] shadow-xl shrink-0">
              <AvatarImage src={user.avatar || user.image} className="object-cover" />
              <AvatarFallback className="text-2xl">{(user.name || '?')[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 pb-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h1 className="text-lg font-bold text-white leading-tight truncate">{user.name}</h1>
                {user.rank && ['watcher', 'legion', 'verified', 'union'].includes(user.rank.toLowerCase()) && (
                  <RankBadge rank={user.rank} size={18} />
                )}
                {age && <span className="text-white/50 text-sm">· {age}</span>}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-white/50 text-sm">@{displayHandle}</span>
                {(user.countryFlag || user.country) && (
                  <FlagImage
                    countryName={user.countryFlag || user.country}
                    className="w-4 aspect-3/2 object-contain rounded"
                  />
                )}
              </div>
              {/* Friend count — sourced from the friendsCount counter on the user doc */}
              {typeof friendsCount === 'number' && (
                <div className="flex items-center gap-1.5 mt-1 text-white/60 text-sm">
                  <Users className="w-4 h-4 shrink-0" />
                  <span>
                    <span className="text-white font-semibold">{friendsCount.toLocaleString()}</span>{' '}
                    {friendsCount === 1 ? 'friend' : 'friends'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {user.bio && (
            <p className="text-white/75 text-sm leading-relaxed mb-4 whitespace-pre-wrap">{user.bio}</p>
          )}

          {/* Tags — 24px gap below the last tag row before the photo grid */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {tags.map((tag, i) => (
                <IdentityTag key={i} tag={tag} variant="card" />
              ))}
            </div>
          )}

          {/* Portfolio thumbnails — 8px gap between grid items */}
          {portfolioImages.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-5">
              {portfolioImages.slice(0, 6).map((src, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-white/5">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Connect + Download CTA */}
        <div className="mx-4 mt-2 mb-10 rounded-2xl bg-gradient-to-br from-[#1c1c1f] to-[#101011] border border-white/10 p-6 shadow-xl">
          <div className="text-center">
            <p className="text-white font-bold text-lg leading-tight mb-1">
              Connect with {user.name?.split(' ')[0] || 'them'} on AfroYap
            </p>
            <p className="text-white/55 text-sm mb-5">
              Send a friend request, yap, and explore the community.
            </p>
          </div>

          {/* Sign up / Log in */}
          <div className="flex gap-3">
            <button
              onClick={goToSignup}
              className="flex-1 h-11 rounded-xl bg-[#4478ea] hover:bg-[#3b6ad6] transition-colors text-white font-bold text-sm flex items-center justify-center gap-2"
            >
              Create account <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={goToLogin}
              className="flex-1 h-11 rounded-xl border border-white/20 hover:bg-white/5 transition-colors text-white font-semibold text-sm"
            >
              Log in
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">or get the app</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* Store badges */}
          <WebOnly>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block transition-transform hover:scale-[1.03] active:scale-95"
              aria-label="Download AfroYap on the App Store"
            >
              <img src={APP_STORE_BADGE} alt="Download on the App Store" className="h-11 w-auto" />
            </a>
            {PLAY_STORE_URL && (
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block transition-transform hover:scale-[1.03] active:scale-95"
                aria-label="Get AfroYap on Google Play"
              >
                <img src={PLAY_STORE_BADGE} alt="Get it on Google Play" className="h-11 w-auto" />
              </a>
            )}
          </div>
          </WebOnly>
        </div>
      </motion.div>
    </div>
  );
}
